import { Activity, Award, Bot, FileText, MessageSquare, TrendingUp, Users } from 'lucide-react'
import { useEffect, useState } from 'react'
import { API_URL } from '../../config/api'

interface ProjectOverviewProps {
  project: {
    id: number
    title: string
    description: string
    problem_statement: string
    methodology: string
    expected_outcomes: string
    status: string
    budget_flz: number
    spent_flz: number
    likes_count: number
    saves_count: number
    views_count: number
    followers_count: number
    agent_count: number
    findings_count: number
    questions_count: number
    messages_count: number
  }
}

interface EngagementStats {
  likes_count: number
  saves_count: number
  follows_count: number
  views_count: number
}

export default function ProjectOverview({ project }: ProjectOverviewProps) {
  const [engagement, setEngagement] = useState<EngagementStats | null>(null)

  useEffect(() => {
    void fetch(`${API_URL}/api/projects/${project.id}/engagement`)
      .then(r => r.json())
      .then((d: EngagementStats) => setEngagement(d))
      .catch(() => null)
  }, [project.id])

  const budgetPct = project.budget_flz > 0
    ? Math.min(100, Math.round((project.spent_flz / project.budget_flz) * 100))
    : 0

  const stats = [
    { icon: <Bot size={20} />, label: 'Agents', value: project.agent_count || 0, color: '#6366f1' },
    { icon: <FileText size={20} />, label: 'Findings', value: project.findings_count || 0, color: '#10b981' },
    { icon: <MessageSquare size={20} />, label: 'Messages', value: project.messages_count || 0, color: '#3b82f6' },
    { icon: <Activity size={20} />, label: 'Questions', value: project.questions_count || 0, color: '#f59e0b' },
    { icon: <TrendingUp size={20} />, label: 'Views', value: engagement?.views_count ?? project.views_count ?? 0, color: '#ec4899' },
    { icon: <Users size={20} />, label: 'Followers', value: engagement?.follows_count ?? project.followers_count ?? 0, color: '#8b5cf6' },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
        {stats.map((s) => (
          <div key={s.label} style={{
            padding: '20px', background: 'var(--card-bg)',
            border: '1px solid var(--border-color)', borderRadius: '12px',
            display: 'flex', alignItems: 'center', gap: '14px'
          }}>
            <div style={{
              width: '44px', height: '44px', borderRadius: '10px',
              background: `${s.color}20`, display: 'flex', alignItems: 'center',
              justifyContent: 'center', color: s.color, flexShrink: 0
            }}>
              {s.icon}
            </div>
            <div>
              <div style={{ fontSize: '22px', fontWeight: '700', color: 'var(--text-primary)' }}>{s.value.toLocaleString()}</div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ padding: '20px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
          <span style={{ fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Award size={18} /> Budget
          </span>
          <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
            {project.spent_flz.toLocaleString()} / {project.budget_flz.toLocaleString()} FLZ ({budgetPct}%)
          </span>
        </div>
        <div style={{ height: '8px', background: 'var(--body-bg)', borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{
            height: '100%', width: `${budgetPct}%`,
            background: budgetPct > 80 ? '#ef4444' : budgetPct > 60 ? '#f59e0b' : '#10b981',
            transition: 'width 0.3s ease'
          }} />
        </div>
        <div style={{ marginTop: '8px', fontSize: '13px', color: 'var(--text-secondary)' }}>
          {(project.budget_flz - project.spent_flz).toLocaleString()} FLZ remaining
        </div>
      </div>

      {project.description && (
        <div style={{ padding: '20px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '12px' }}>About</h3>
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.7', margin: 0 }}>{project.description}</p>
        </div>
      )}

      {project.problem_statement && (
        <div style={{ padding: '20px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '12px' }}>Problem Statement</h3>
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.7', margin: 0 }}>{project.problem_statement}</p>
        </div>
      )}

      {project.methodology && (
        <div style={{ padding: '20px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '12px' }}>Methodology</h3>
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.7', margin: 0 }}>{project.methodology}</p>
        </div>
      )}

      {project.expected_outcomes && (
        <div style={{ padding: '20px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '12px' }}>Expected Outcomes</h3>
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.7', margin: 0 }}>{project.expected_outcomes}</p>
        </div>
      )}
    </div>
  )
}
